import './style.css';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Alert } from '../shared/components/alert/Alert';
import { Button } from '../shared/components/button/Button';
import LoadingSpinner from '../shared/components/UIElements/LoadingSpinner';
import { useHttpClient } from '../shared/hooks/http-hook';
import { handleEmailChange } from '../shared/util/handleValueChange';
import { RightPanelContainer } from './layout/RightPanelContainer';

export function RequestPasswordReset() {
  const location = useLocation();
  const { passcode } = location.state || {};
  const { isLoading, error, sendRequest } = useHttpClient();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string | undefined>();
  const [emailError, setEmailError] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const resetHandler = async (event) => {
    event.preventDefault();

    // Clear previous messages
    setBackendError(null);
    setSuccessMessage(null);

    // Validate email
    if (!email?.trim()) {
      setValidationError(t('signin.email.error'));
      return;
    }

    if (emailError) {
      setValidationError(t('signin.email.error'));
      return;
    }

    // Clear validation error if email is valid
    setValidationError(null);

    const url = passcode
      ? `${process.env.REACT_APP_BACKEND_URL}/users/resetPasscode`
      : `${process.env.REACT_APP_BACKEND_URL}/users/reset`;

    try {
      const responseData = await sendRequest(
        url,
        'POST',
        JSON.stringify({
          email,
        })
      );

      if (responseData.message) {
        setSuccessMessage(responseData.message);
      }
    } catch (err) {
      setBackendError(typeof err === 'string' ? err : error || 'An error occurred');
    }
  };

  const handleEmailInputChange = (e) => {
    // Clear errors when user starts typing
    setValidationError(null);
    setBackendError(null);
    handleEmailChange(e, setEmail, setEmailError);
  };

  return (
    <RightPanelContainer type="requestPasswordReset">
      <form onSubmit={resetHandler}>
        <h1>{passcode ? t('label.reset_passcode') : t('R042')}</h1>
        <p className="description">{passcode ? t('R124') : t('R081')}</p>
        <label className="not_logged_label" htmlFor="email">
          {t('R023')}
        </label>
        <input
          type="email"
          className="code_input "
          id="email"
          name="email"
          aria-label={t('R023')}
          onChange={(e) => handleEmailInputChange(e)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              e.preventDefault();
              resetHandler(e);
            }
          }}
          data-testid="reset-password-email-input"
        />
        {isLoading && <LoadingSpinner />}

        {validationError && (
          <Alert
            type="error"
            message={validationError}
            id="validation-error"
            isVisible={!!validationError}
            isAssessive
          />
        )}

        {backendError && (
          <Alert
            type="error"
            message={backendError}
            id="backend-error"
            isVisible={!!backendError}
            isAssessive
            dataTestId="reset-password-backend-error"
          />
        )}

        {successMessage && (
          <Alert
            type="success"
            message={successMessage}
            id="success-message"
            isVisible={!!successMessage}
            isAssessive
            dataTestId="reset-password-success-message"
          />
        )}

        {!isLoading && (
          <Button
            type="submit"
            onClick={resetHandler}
            id="continue"
            dataTestId="reset-password-submit-button"
          >
            {t('R044')}
          </Button>
        )}
      </form>
    </RightPanelContainer>
  );
}
